// src/app/services/reporte-pdf.service.ts
import { Injectable, inject } from '@angular/core';
import { MissionCurrencyService } from './mission-currency.service';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

@Injectable({ providedIn: 'root' })
export class ReportePdfService {

    private readonly missionCurrency = inject(MissionCurrencyService);
    private pdfMake!: any;
    /** Carga pdfmake y vfs en caliente y los “conecta” de forma segura */
    private async initPdfMake() {
        if (this.pdfMake) return;

        const pdfMakeMod = await import('pdfmake/build/pdfmake');
        const vfsFontsMod = await import('pdfmake/build/vfs_fonts');

        // Soporta default/namespace según bundler:
        const pdfMake: any = (pdfMakeMod as any).default ?? pdfMakeMod;
        const vfsFonts: any = (vfsFontsMod as any).default ?? vfsFontsMod;

        // Intento de localizar VFS en varias ubicaciones posibles
        let vfs = vfsFonts?.pdfMake?.vfs ?? vfsFonts?.vfs;

        // Si falla, verificar si se adjuntó al objeto global (común en scripts antiguos o mix de módulos)
        if (!vfs && (window as any).pdfMake?.vfs) {
            vfs = (window as any).pdfMake.vfs;
        }

        // Si sigue fallando, verificar si vfsFonts es el objeto VFS directamente (keys terminan en .ttf/.woff)
        if (!vfs && vfsFonts && Object.keys(vfsFonts).some(k => k.endsWith('.ttf') || k.endsWith('.woff'))) {
            vfs = vfsFonts;
        }

        if (!vfs) {
            // Último recurso: asignar objeto vacío para evitar crash inmediato, aunque las fuentes fallarán
            // vfs = {}; 
            throw new Error('No se pudo cargar VFS de pdfmake');
        }

        pdfMake.vfs = vfs;
        this.pdfMake = pdfMake;
    }

    private async imgToDataUrl(path: string): Promise<string> {
        try {
            const res = await fetch(path);
            if (!res.ok) throw new Error(`Failed to load image: ${path} ${res.statusText}`);
            const blob = await res.blob();
            return await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (e) {
            return ''; // Return empty string or a fallback placeholder if needed to prevent crash
        }
    }

    async descargar(informe: any) {
        await this.initPdfMake();
        const logo = await this.imgToDataUrl('assets/images/logo_mmm.png');

        const dd: any = {
            pageSize: 'A4',
            pageMargins: [40, 110, 40, 60],
            header: (currentPage: number, pageCount: number) => ({
                margin: [40, 30, 40, 0],
                columns: [
                    { image: logo, width: 60 },
                    {
                        stack: [
                            { text: 'MOVIMIENTO MISIONERO MUNDIAL', style: 'h1' },
                            { text: 'IGLESIA CRISTIANA PENTECOSTÉS DEL (ESPAÑA)', style: 'h2' },
                            { text: 'INFORME MENSUAL DEL MINISTERIO', style: 'h3' },
                            { text: `IGLESIA: ${informe.iglesia}     Mes-Año: ${informe.mes} ${informe.anio}`, style: 'hdrLine' },
                        ],
                        width: '*',
                    },
                ],
            }),
            footer: (currentPage: number, pageCount: number) => ({
                margin: [40, 0, 40, 20],
                columns: [
                    { text: '“Solícitos en guardar la unidad del Espíritu en el vínculo de la paz” (Ef. 4:3)', italics: true, fontSize: 9 },
                    { text: `${currentPage} / ${pageCount}`, alignment: 'right', fontSize: 9 },
                ],
            }),
            defaultStyle: { fontSize: 9 },
            styles: {
                h1: { bold: true, fontSize: 12, margin: [0, 0, 0, 2] },
                h2: { fontSize: 10, margin: [0, 0, 0, 2] },
                h3: { bold: true, fontSize: 11, margin: [0, 4, 0, 6] },
                hdrLine: { margin: [0, 2, 0, 0] },
                secTitle: { bold: true, margin: [0, 12, 0, 6] },
                right: { alignment: 'right' },
                small: { fontSize: 8 },
            },
            content: [
                { text: 'I. INFORME ESPIRITUAL', style: 'secTitle' },
                {
                    table: {
                        widths: ['*', 60],
                        body: [
                            ['1  Miembros bautizados activos', { text: informe.e1, style: 'right' }],
                            ['2  Creyentes bautizados no reconocidos', { text: informe.e2, style: 'right' }],
                            ['3  Nuevos creyentes', { text: informe.e3, style: 'right' }],
                            ['4  Miembros bautizados en Espíritu Santo', { text: informe.e4, style: 'right' }],
                            ['5  Profesiones de fe durante el mes', { text: informe.e5, style: 'right' }],
                            ['6  Promedio asistencia Escuela dominical', { text: informe.e6, style: 'right' }],
                        ],
                    },
                    layout: 'lightHorizontalLines',
                },

                { text: 'II. INFORME FINANCIERO', style: 'secTitle' },
                { text: 'A. INGRESOS', bold: true, margin: [0, 6, 0, 4] },
                {
                    table: {
                        widths: ['*', 70],
                        body: [
                            ['1  Diezmo Iglesia', { text: this.eur(informe.ing.diezmoIglesia), style: 'right' }],
                            ['2  Ofrendas Diarias', { text: this.eur(informe.ing.ofrendasDiarias), style: 'right' }],
                            ['3  Ofrendas Escuela Dominical', { text: this.eur(informe.ing.ofrendasEscDominical), style: 'right' }],
                            ['4  Ofrendas (Programa Radial)', { text: this.eur(informe.ing.programaRadial), style: 'right' }],
                            ['5  Ofrenda Misionera', { text: this.eur(informe.ing.misionera), style: 'right' }],
                            ['6  Ofrenda predicador', { text: this.eur(informe.ing.predicador), style: 'right' }],
                            ['7  Ofrenda especial o Pro-templo', { text: this.eur(informe.ing.especial), style: 'right' }],
                            ['8  Ayuda del Fondo Nal. o Internac.', { text: this.eur(informe.ing.ayudaFondo), style: 'right' }],
                            [{ text: 'TOTAL INGRESO GENERAL IGLESIA', bold: true }, { text: this.eur(informe.ing.totalGeneral), style: 'right' }],
                        ],
                    },
                    layout: 'lightHorizontalLines',
                },

                { text: 'B.1 EGRESOS (Locales)', bold: true, margin: [0, 10, 0, 4] },
                {
                    table: {
                        widths: ['*', 70],
                        body: [
                            ['1  Alquiler local iglesia', { text: this.eur(informe.eg1.alquilerLocal), style: 'right' }],
                            ['2  Pago de luz, agua, gas, Teléfono', { text: this.eur(informe.eg1.serviciosIglesia), style: 'right' }],
                            ['3  Pago de impuesto - Iglesia (IRPF)', { text: this.eur(informe.eg1.impuestos), style: 'right' }],
                            ['4  Mantenimiento y mejoras del templo', { text: this.eur(informe.eg1.mantenimientoTemplo), style: 'right' }],
                            ['5  Gastos de oficina, copias, envíos', { text: this.eur(informe.eg1.gastosOficina), style: 'right' }],
                            ['6  Compra de Mob., equipos, instrum.', { text: this.eur(informe.eg1.equipamiento), style: 'right' }],
                            ['7  Seguridad Social', { text: this.eur(informe.eg1.seguridadSocial), style: 'right' }],
                            ['8  Alquiler Casa Pastoral', { text: this.eur(informe.eg1.alquilerCasaPastoral), style: 'right' }],
                            ['9  Luz/Agua/Gas Casa Pastoral', { text: this.eur(informe.eg1.serviciosCasaPastoral), style: 'right' }],
                            ['10 Transportes, Viáticos', { text: this.eur(informe.eg1.transportes), style: 'right' }],
                            ['11 Pago préstamos', { text: this.eur(informe.eg1.pagosPrestamos), style: 'right' }],
                            ['12 Programa radial', { text: this.eur(informe.eg1.programaRadial), style: 'right' }],
                            ['13 Sostén del pastor', { text: this.eur(informe.eg1.sostenPastor), style: 'right' }],
                            ['14 Otros (Ayudas, comidas, Esc. Dom.)', { text: this.eur(informe.eg1.otros), style: 'right' }],
                            ['15 Ofrenda al predicador', { text: this.eur(informe.eg1.ofrendaPredicador), style: 'right' }],
                            ['16 Impuesto al vehículo', { text: this.eur(informe.eg1.impuestoVehiculo), style: 'right' }],
                            ['17 Mantenimiento del vehículo', { text: this.eur(informe.eg1.mantVehiculo), style: 'right' }],
                            ['18 Parking', { text: this.eur(informe.eg1.parking), style: 'right' }],
                            [{ text: 'SUB-TOTAL (B.1)', bold: true }, { text: this.eur(informe.eg1.subtotal), style: 'right' }],
                        ],
                    },
                    layout: 'lightHorizontalLines',
                },

                { text: 'B.2 EGRESO NACIONAL', bold: true, margin: [0, 10, 0, 4] },
                {
                    table: {
                        widths: ['*', 70],
                        body: [
                            ['1  Diezmos de diezmos y ofrendas', { text: this.eur(informe.eg2.diezmoDeDiezmos), style: 'right' }],
                            ['2  Ofrenda Misionera', { text: this.eur(informe.eg2.misionera), style: 'right' }],
                            ['3  Ofrenda Pro-convención', { text: this.eur(informe.eg2.proConvencion), style: 'right' }],
                            ['4  Ofrenda confraternidad', { text: this.eur(informe.eg2.confraternidad), style: 'right' }],
                            ['5  Cuota FEREDE', { text: this.eur(informe.eg2.ferede), style: 'right' }],
                            ['8  Otras ofrendas y pagos (Diezmos pastor)', { text: this.eur(informe.eg2.otras), style: 'right' }],
                            [{ text: 'SUB-TOTAL (B.2)', bold: true }, { text: this.eur(informe.eg2.subtotal), style: 'right' }],
                            [{ text: 'TOTAL ENVIADO FONDO NACIONAL', bold: true }, { text: this.eur(informe.eg2.totalFondoNal), style: 'right' }],
                        ],
                    },
                    layout: 'lightHorizontalLines',
                },

                { text: 'C. BALANCE DEL MES', style: 'secTitle' },
                {
                    table: {
                        widths: ['*', 90],
                        body: [
                            ['Total General de Ingresos', { text: this.eur(informe.balance.totalIngresos), style: 'right' }],
                            ['Total General de Egresos (B.1 + B.2)', { text: this.eur(informe.balance.totalEgresos), style: 'right' }],
                            ['Saldo en caja del mes anterior', { text: this.eur(informe.balance.saldoAnterior), style: 'right' }],
                            ['Saldo en caja del mes', { text: this.eur(informe.balance.saldoMes), style: 'right' }],
                            [{ text: 'SALDO FINAL DEL MES', bold: true }, { text: this.eur(informe.balance.saldoFinal), style: 'right' }],
                        ],
                    },
                    layout: 'lightHorizontalLines',
                },

                { text: 'III. INFORME DEL OBRERO', style: 'secTitle' },
                {
                    table: {
                        widths: ['*', 40, '*', 40, '*', 40],
                        body: [
                            ['1 Efectuó culto Misionero', this.siNo(informe.obrero.cultoMisionero),
                                '2 Efectuó Santa Cena', this.siNo(informe.obrero.santaCena),
                                '3 Confraternidad', this.siNo(informe.obrero.confraternidad)],
                            ['4 Visita del presbítero', this.siNo(informe.obrero.visitaPresbitero),
                                '5 Efectuó visitas', this.siNo(informe.obrero.visitas),
                                '6 Evangelismo', this.siNo(informe.obrero.evangelismo)],
                            ['7 Fundó campos blancos', this.siNo(informe.obrero.camposBlancos),
                                '8 Formó obreros', this.siNo(informe.obrero.formoObreros),
                                '9 Ayuda social', this.siNo(informe.obrero.ayudaSocial)],
                            ['10 Efectuó ayuno', this.siNo(informe.obrero.ayuno), '', '', '', ''],
                        ],
                    },
                    layout: 'lightHorizontalLines',
                },

                { text: 'OBSERVACIONES', style: 'secTitle' },
                { text: informe.observaciones || '', margin: [0, 0, 0, 12] },

                {
                    columns: [
                        { text: `PASTOR: ${informe.pastor || ''}`, width: '*', margin: [0, 20, 0, 0] },
                        { text: `FIRMA DEL PASTOR: ${informe.firmaPastor || ''}`, width: '*', alignment: 'right', margin: [0, 20, 0, 0] },
                    ],
                },
            ],
        };

        this.pdfMake.createPdf(dd).download(`informe_${informe?.mes || ''}_${informe?.anio || ''}.pdf`);
    }

    private eur(v: number | null | undefined): string {
        return this.missionCurrency.format(v);
    }
    private siNo(b: boolean | null | undefined): string { return b ? 'Si' : 'No'; }

    async generarCertificadoDonaciones(data: any) {
        await this.initPdfMake();
        const logo = await this.imgToDataUrl('assets/images/logo_mmm.png');
        const docDefinition = this.getCertificateDefinition(data, logo);
        this.pdfMake.createPdf(docDefinition).download(`Certificado_Donaciones_${data.anio}_${(data.miembroNombre || '').replace(/\s+/g, '_')}.pdf`);
    }

    async generarCertificadosMasivos(datas: any[], filename: string) {
        await this.initPdfMake();
        const logo = await this.imgToDataUrl('assets/images/logo_mmm.png');

        const fullContent: any[] = [];

        datas.forEach((data, index) => {
            const def = this.getCertificateDefinition(data, logo);
            // Extraer solo el contenido
            fullContent.push(...def.content);
            if (index < datas.length - 1) {
                fullContent.push({ text: '', pageBreak: 'after' });
            }
        });

        const dd = {
            pageSize: 'A4',
            pageMargins: [40, 40, 40, 60],
            // Header is tricky for batch if it's dynamic per page?
            // pdfmake header can be a function. But here we have different headers per certificate?
            // Actually the header contains the logo and title. It's the same for all.
            // But if we want it on every page...
            // pdfmake header is global for the doc.
            header: {
                margin: [40, 20, 40, 0],
                columns: [
                    { image: logo, width: 50 },
                    {
                        text: 'MOVIMIENTO MISIONERO MUNDIAL\nIGLESIA CRISTIANA PENTECOSTÉS DE ESPAÑA',
                        style: 'headerText',
                        alignment: 'center',
                        margin: [0, 10, 0, 0]
                    },
                    { text: '', width: 50 }
                ]
            },
            content: fullContent,
            styles: this.getStyles()
        };

        this.pdfMake.createPdf(dd).download(filename);
    }

    private getCertificateDefinition(data: any, logo: string): any {
        return {
            pageSize: 'A4',
            pageMargins: [40, 40, 40, 60],
            header: {
                margin: [40, 20, 40, 0],
                columns: [
                    { image: logo, width: 50 },
                    {
                        text: 'MOVIMIENTO MISIONERO MUNDIAL\nIGLESIA CRISTIANA PENTECOSTÉS DE ESPAÑA',
                        style: 'headerText',
                        alignment: 'center',
                        margin: [0, 10, 0, 0]
                    },
                    { text: '', width: 50 }
                ]
            },
            content: [
                { text: 'CERTIFICADO DE DONACIONES', style: 'title', margin: [0, 20, 0, 20] },

                {
                    text: [
                        { text: 'D./Dña. ', bold: true },
                        `${data.responsable || '..........................................................'}\n`,
                        { text: 'en calidad de ', bold: true },
                        `${data.cargo || 'Tesorero/a'}\n`,
                        { text: 'de la entidad ', bold: true },
                        `${data.iglesiaNombre || 'MOVIMIENTO MISIONERO MUNDIAL'}\n`,
                        { text: 'con NIF ', bold: true },
                        `${data.iglesiaNif || 'R-2800669-G'}\n`,
                        { text: 'y domicilio en ', bold: true },
                        `${data.iglesiaDireccion || '..........................................................'}`
                    ],
                    margin: [0, 0, 0, 20]
                },

                { text: 'CERTIFICA', style: 'h2', alignment: 'center', margin: [0, 0, 0, 10] },

                {
                    text: [
                        'Que D./Dña. ',
                        { text: data.miembroNombre, bold: true },
                        ' con NIF/NIE ',
                        { text: data.miembroNif || '...................', bold: true },
                        ' y domicilio en ',
                        { text: data.miembroDireccion || '..........................................................', bold: true },
                        ` ha realizado durante el ejercicio ${data.anio} donativos a esta entidad por el importe y concepto detallados a continuación:`
                    ],
                    alignment: 'justify',
                    margin: [0, 0, 0, 20]
                },

                {
                    table: {
                        headerRows: 1,
                        widths: ['*', '*', 'auto'],
                        body: [
                            [
                                { text: 'FECHA', style: 'tableHeader' },
                                { text: 'CONCEPTO', style: 'tableHeader' },
                                { text: 'IMPORTE', style: 'tableHeader', alignment: 'right' }
                            ],
                            ...data.donaciones.map((d: any) => [
                                new Date(d.fecha).toLocaleDateString('es-ES'),
                                'Diezmo / Donación',
                                { text: this.eur(d.monto), alignment: 'right' }
                            ]),
                            [
                                { text: 'TOTAL', bold: true, colSpan: 2, alignment: 'right' },
                                {},
                                { text: this.eur(data.total), bold: true, alignment: 'right' }
                            ]
                        ]
                    },
                    layout: 'lightHorizontalLines',
                    margin: [0, 0, 0, 30]
                },

                {
                    text: `Y para que conste a los efectos oportunos ante la Administración Tributaria, expido el presente certificado en ${data.ciudadFirma || 'Madrid'} a ${new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}.`,
                    alignment: 'justify',
                    margin: [0, 0, 0, 40]
                },

                {
                    columns: [
                        {
                            stack: [
                                { text: 'Vº Bº El Pastor/Presidente', bold: true },
                                { text: '\n\n\n\n' },
                                { text: 'Fdo: ...............................................' }
                            ],
                            alignment: 'center'
                        },
                        {
                            stack: [
                                { text: 'El/La Tesorero/a', bold: true },
                                { text: '\n\n\n\n' },
                                { text: 'Fdo: ...............................................' }
                            ],
                            alignment: 'center'
                        }
                    ]
                }
            ],
            styles: this.getStyles()
        };
    }

    private getStyles() {
        return {
            headerText: { fontSize: 10, bold: true },
            title: { fontSize: 16, bold: true, alignment: 'center', decoration: 'underline' },
            h2: { fontSize: 14, bold: true },
            tableHeader: { bold: true, fontSize: 10, fillColor: '#eeeeee' },
            h1: { bold: true, fontSize: 12, margin: [0, 0, 0, 2] },
            h3: { bold: true, fontSize: 11, margin: [0, 4, 0, 6] },
            hdrLine: { margin: [0, 2, 0, 0] },
            secTitle: { bold: true, margin: [0, 12, 0, 6] },
            right: { alignment: 'right' },
            small: { fontSize: 8 },
        };
    }

    async generarListadoDiezmosAnual(data: any) {
        await this.initPdfMake();
        const logo = await this.imgToDataUrl('assets/images/logo_mmm.png');

        const dd: any = {
            pageSize: 'A4',
            pageMargins: [40, 110, 40, 60],
            header: (currentPage: number, pageCount: number) => ({
                margin: [40, 30, 40, 0],
                columns: [
                    { image: logo, width: 60 },
                    {
                        stack: [
                            { text: 'MOVIMIENTO MISIONERO MUNDIAL', style: 'h1', alignment: 'center' },
                            { text: data.iglesiaNombre, style: 'h2', alignment: 'center', margin: [0, 5, 0, 0] },
                            { text: `LISTADO DE DIEZMOS Y DONACIONES - AÑO ${data.anio}`, style: 'h3', alignment: 'center', margin: [0, 10, 0, 0] },
                        ],
                        width: '*',
                    },
                ],
            }),
            footer: (currentPage: number, pageCount: number) => ({
                margin: [40, 0, 40, 20],
                columns: [
                    { text: 'Consulta interna', italics: true, fontSize: 9 },
                    { text: `${currentPage} / ${pageCount}`, alignment: 'right', fontSize: 9 },
                ],
            }),
            content: [
                {
                    table: {
                        headerRows: 1,
                        widths: ['*', 'auto', 'auto'],
                        body: [
                            [
                                { text: 'NOMBRE COMPLETO', style: 'tableHeader' },
                                { text: 'DNI / NIE', style: 'tableHeader', alignment: 'center' },
                                { text: 'TOTAL APORTADO', style: 'tableHeader', alignment: 'right' }
                            ],
                            ...data.miembros.map((m: any) => [
                                { text: m.nombreCompleto, style: 'bodyText' },
                                { text: m.documento || '-', style: 'bodyText', alignment: 'center' },
                                { text: this.eur(m.total), style: 'bodyText', alignment: 'right' }
                            ]),
                            [
                                { text: 'TOTAL GENERAL', style: 'tableHeader', colSpan: 2, alignment: 'right' },
                                {},
                                { text: this.eur(data.totalGeneral), style: 'tableHeader', alignment: 'right' }
                            ]
                        ]
                    },
                    layout: {
                        fillColor: function (rowIndex: number) {
                            return (rowIndex % 2 === 0) ? '#f9fafb' : null;
                        },
                        hLineWidth: function (i: number, node: any) {
                            return (i === 0 || i === node.table.body.length) ? 0.5 : 0.5;
                        },
                        vLineWidth: function (i: number, node: any) {
                            return 0;
                        },
                        hLineColor: function (i: number, node: any) {
                            return '#e5e7eb';
                        }
                    }
                }
            ],
            styles: {
                h1: { bold: true, fontSize: 16, color: '#1f2937' },
                h2: { bold: true, fontSize: 14, color: '#374151' },
                h3: { bold: true, fontSize: 12, color: '#4b5563' },
                tableHeader: { bold: true, fontSize: 10, color: '#ffffff', fillColor: '#374151', margin: [0, 5, 0, 5] },
                bodyText: { fontSize: 10, color: '#1f2937', margin: [0, 5, 0, 5] }
            }
        };

        this.pdfMake.createPdf(dd).download(`Listado_Diezmos_${data.anio}.pdf`);
    }
}
